import React, { useState } from 'react';

import moment from 'moment';
import { useHistory } from 'react-router';

import { MenuItem } from '@material-ui/core';

import { AnnouncementStatusDialog } from '../../employee-messages/announcement-composer/announcement-status-dialog/announcement-status-dialog';
import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { UiKebabMenu } from '../../ui/ui-kebab-menu';
import { employeeMessagesRoute } from '../../web-routes';

import { EmailMessageHeader } from './email-message-header';
import { MessageLanguage } from './message-item';
import { TextMessageHeader } from './text-message-header';

import { LanguageCode, LANGUAGES } from '../../util/languages';
import { MESSAGE_TYPE, MessageType } from '../../util/message-types';
import { VCardMessage } from '../../widgets/contact-card/v-card-message';

type InputProps = {
  hideHeaderActions: boolean;

  orgId?: number;
  sentWithVCard: boolean;
  isScheduledToSend: boolean;
  forceAllowEditing: boolean;

  messageSid?: string;
  messageType?: MessageType;
  subject?: string | null;
  senderName?: string;
  sendDateTime?: string | Date;
  recipientName?: string;
  timeZone?: string;
  announcement?: Announcement | null | undefined;
  isAnnouncementThread?: boolean;

  // Kebab actions
  onEditMessage?: () => void;
  onCancelMessage?: () => void;
  onDeleteMessage?: () => void;
  allowLinkToAnnouncement?: boolean;

  // View other languages props
  disableOtherLanguagesMessage: boolean;
  otherLanguages: Array<MessageLanguage> | undefined;
  hiddenLanguages: Array<MessageLanguage> | undefined;
  onOtherLanguageClick: (languageCode: LanguageCode) => () => void;
};

export const MessageItemHeader = (props: InputProps) => {
  const {
    hideHeaderActions,

    messageSid,
    messageType,
    senderName,
    sendDateTime,
    timeZone,
    subject,
    sentWithVCard,
    orgId,
    recipientName,
    isScheduledToSend,
    forceAllowEditing,
    isAnnouncementThread,
    announcement,

    // Kebab actions
    onEditMessage,
    onCancelMessage,
    onDeleteMessage,
    allowLinkToAnnouncement,

    // View other languages props
    disableOtherLanguagesMessage,
    otherLanguages,
    hiddenLanguages,
    onOtherLanguageClick,
  } = props;

  const [showMessageFailureDialog, setShowMessageFailureDialog] = useState(false);

  const canViewAnnouncementDeliveryDetails = isAnnouncementThread && announcement;
  const handleMessageFailureClick = () => {
    if (isAnnouncementThread && announcement) {
      ProductTelemetryController.logEvent('clicked view announcement deliverability', {
        value: announcement.announcementId,
      });
    }

    setShowMessageFailureDialog(true);
  };
  const handleMessageFailureClose = () => setShowMessageFailureDialog(false);

  const history = useHistory();
  const isEmail = messageType === MESSAGE_TYPE.EMAIL;
  const formattedSendDateTime = `${moment(sendDateTime).format('dddd, MMMM Do [at] h:mma')}${
    timeZone ? ` ${timeZone}` : ''
  }`;

  const isEditAllowed =
    forceAllowEditing || !sendDateTime || moment(sendDateTime).isAfter(moment());

  const handleLinkToAnnouncement = () => {
    history.push(
      employeeMessagesRoute({
        orgId: announcement?.EmployeeGroup?.orgId,
        groupId: announcement?.EmployeeGroup?.employeeGroupId,
      }),
    );
  };

  const actions = () => {
    const kebabActions = [] as Array<React.ReactElement>;
    if (hideHeaderActions) {
      return kebabActions;
    }

    if (canViewAnnouncementDeliveryDetails) {
      kebabActions.push(
        <MenuItem key="view-details" onClick={handleMessageFailureClick}>
          Details
        </MenuItem>,
      );
    }

    if (isEditAllowed) {
      if (onEditMessage) {
        kebabActions.push(
          <MenuItem key="edit-option" onClick={onEditMessage} data-cy="edit-message">
            Edit
          </MenuItem>,
        );
      }
      if (onCancelMessage) {
        kebabActions.push(
          <MenuItem key="cancel-option" onClick={onCancelMessage}>
            Cancel
          </MenuItem>,
        );
      }
      if (onDeleteMessage) {
        kebabActions.push(
          <MenuItem key="delete-option" onClick={onDeleteMessage}>
            Delete
          </MenuItem>,
        );
      }
    }

    if (allowLinkToAnnouncement && announcement?.EmployeeGroup?.employeeGroupId) {
      kebabActions.push(
        <MenuItem key="group-option" onClick={handleLinkToAnnouncement}>
          View Announcement
        </MenuItem>,
      );
    }

    if (!disableOtherLanguagesMessage && otherLanguages && hiddenLanguages) {
      hiddenLanguages.forEach(({ languageCode }) =>
        kebabActions.push(
          <MenuItem
            key={languageCode}
            onClick={onOtherLanguageClick(languageCode)}
            data-cy="message-translate"
          >
            View {LANGUAGES[languageCode]} version
          </MenuItem>,
        ),
      );
    }

    return kebabActions;
  };

  const hasActions = !!actions().length;
  const showVCard = sentWithVCard && orgId;
  const hasHeader = senderName || showVCard || hasActions;

  if (!hasHeader) {
    return null;
  }

  return (
    <div className={`u-margin-x-md-neg ${isEmail || !hasActions ? 'u-padding-bottom' : ''}`}>
      <div
        className={`u-padding-x ${
          isEmail ? `u-border-bottom u-border-neutral6 u-padding-bottom` : ''
        }`}
      >
        <div
          className={`u-display-flex u-justifyContent-spaceBetween ${
            hasActions ? 'u-margin-top' : ''
          }`}
        >
          {isEmail ? (
            <EmailMessageHeader
              senderName={senderName}
              recipientName={recipientName}
              subject={subject}
              formattedSendDateTime={formattedSendDateTime}
            />
          ) : (
            <TextMessageHeader
              senderName={senderName}
              isScheduledToSend={isScheduledToSend}
              formattedSendDateTime={formattedSendDateTime}
            />
          )}
          {showVCard ? <VCardMessage orgId={orgId} isScheduledMessage={isScheduledToSend} /> : null}
          {hasActions ? (
            <div className="u-margin-top-lg-neg">
              <UiKebabMenu size="sm" menuId={`message-${messageSid}-actions`}>
                {actions()}
              </UiKebabMenu>
            </div>
          ) : null}
        </div>
      </div>
      {canViewAnnouncementDeliveryDetails && showMessageFailureDialog ? (
        <AnnouncementStatusDialog
          announcementId={announcement.announcementId}
          onClose={handleMessageFailureClose}
        />
      ) : null}
    </div>
  );
};
